import { sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import type {
  IServerSideDatasource,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridReadyEvent,
  ModelUpdatedEvent,
  PaginationChangedEvent,
} from 'ag-grid-enterprise';
import { useCallback, useMemo, useReducer, useRef } from 'react';

import { getInitialState, sampleTableGridReducer } from './useSampleTableGridReducer';

// AG Grid quartz theme row heights per density (normal = default)
const ROW_HEIGHT_PX = 32; // normal density
const HEADER_HEIGHT_PX = 40;
const PAGINATION_BAR_HEIGHT_PX = 52;

interface UseSampleTableGridProps<T> {
  studyId: string;
  tableId: string;
  validSortFields: string[];
  paginationPageSizeKeyFn: (studyId: string) => string;
  defaultPageSize?: number;
  datasource: IServerSideDatasource<T>;
  autoHeight?: boolean; // when true, skip container height calculation
}

export function useSampleTableGrid<T>({
  studyId,
  tableId,
  validSortFields,
  paginationPageSizeKeyFn,
  defaultPageSize = 25,
  datasource,
  autoHeight = false,
}: UseSampleTableGridProps<T>) {
  const [state, dispatch] = useReducer(sampleTableGridReducer, undefined, () =>
    getInitialState(studyId, paginationPageSizeKeyFn, defaultPageSize),
  );
  const firstDataRenderedRef = useRef(false);

  /**
   * Compute an explicit pixel height for the AG Grid container so the grid
   * shows all rows on the *current page* without an internal vertical scrollbar.
   *
   * We track currentPageRowCount separately (updated in handlePaginationChanged)
   * so the height reflects the actual rows rendered — not the total across all
   * pages, which would over-size the container on the last (partial) page and
   * when a large page-size option is selected but fewer rows exist.
   *
   * Falls back to pageSize until the first data is rendered.
   */
  const gridContainerHeight = useMemo(() => {
    if (autoHeight) return undefined;
    const visibleRows = state.currentPageRowCount ?? state.pageSize;
    return visibleRows * ROW_HEIGHT_PX + HEADER_HEIGHT_PX + PAGINATION_BAR_HEIGHT_PX;
  }, [autoHeight, state.currentPageRowCount, state.pageSize]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    [],
  );

  const handleGridReady = useCallback(
    (e: GridReadyEvent<T>) => {
      const currentSort = e.api
        .getColumnState()
        .find((col: { sort?: string | null; colId?: string }) => col.sort === 'asc' || col.sort === 'desc');
      if (!currentSort || (currentSort.colId && !validSortFields.includes(currentSort.colId))) {
        e.api.applyColumnState({
          state: [{ colId: validSortFields[0], sort: 'asc' }],
          defaultState: { sort: null },
        });
      }
      e.api.setGridOption('serverSideDatasource', datasource);
      e.api.updateGridOptions({ paginationPageSize: state.pageSize });
      sizeColumnsOnFirstVisit(e.api, tableId);
    },
    [validSortFields, datasource, state.pageSize, tableId],
  );

  const handleFirstDataRendered = useCallback((e: FirstDataRenderedEvent<T>) => {
    firstDataRenderedRef.current = true;
    dispatch({ type: 'SET_FIRST_DATA_RENDERED', value: true });
    const rowCount = e.api.paginationGetRowCount();
    if (rowCount >= 0) {
      dispatch({ type: 'SET_TOTAL_COUNT', value: rowCount });
    }
  }, []);

  const handleFilterChanged = useCallback((e: FilterChangedEvent<T>) => {
    if (!firstDataRenderedRef.current) return;
    const rowCount = e.api.paginationGetRowCount();
    if (rowCount >= 0) {
      dispatch({ type: 'SET_TOTAL_COUNT', value: rowCount });
    }
  }, []);

  const handleModelUpdated = useCallback((e: ModelUpdatedEvent<T>) => {
    if (!firstDataRenderedRef.current) return;
    const rowCount = e.api.paginationGetRowCount();
    if (rowCount >= 0) {
      dispatch({ type: 'SET_TOTAL_COUNT', value: rowCount });
    }
  }, []);

  const handlePaginationChanged = useCallback(
    (e: PaginationChangedEvent<T>) => {
      const newPageSize = e.api.paginationGetPageSize();
      dispatch({ type: 'SET_PAGE_SIZE', value: newPageSize });
      localStorage.setItem(paginationPageSizeKeyFn(studyId), newPageSize.toString());

      // Compute the actual row count on the current page so gridContainerHeight
      // doesn't over-size on the last (partial) page or with large page-size options.
      const totalRows = e.api.paginationGetRowCount();
      const currentPage = e.api.paginationGetCurrentPage();
      const rowsBeforePage = currentPage * newPageSize;
      const currentPageRowCount = Math.max(0, Math.min(newPageSize, totalRows - rowsBeforePage));
      dispatch({ type: 'SET_CURRENT_PAGE_ROW_COUNT', value: currentPageRowCount });
    },
    [paginationPageSizeKeyFn, studyId],
  );

  return {
    pageSize: state.pageSize,
    totalCount: state.totalCount,
    firstDataRenderedRef,
    gridContainerHeight,
    defaultColDef,
    handleGridReady,
    handleFirstDataRendered,
    handleFilterChanged,
    handleModelUpdated,
    handlePaginationChanged,
    dispatch,
  };
}
